import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {
  DirectusService,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
} from '@frontend/directus';
import {
  CriteriaWeightingResponse,
  EvaluationCriteriaControllerService,
  EvaluationCriteriaResponse,
  EvaluationSubCriteriaComparisonControllerService,
  EvaluationSubCriteriaResponse,
  EvaluationSubCriteriaWeightingControllerService,
  PairwiseComparisonRequest,
  PairwiseComparisonsResponse,
  ProjectChapterType,
  ProjectStepType,
  UpsertCriteriaWeightingRequest,
} from '@frontend/shared-api';
import {SubCriteriaEvaluationService} from '../../../../../../shared/services/sub-criteria-evaluation.service';
import {SubCriteriaInclusionX} from './step-4-2-sub-criteria-evaluation-x.types';

@Injectable({providedIn: 'root'})
export class Step42SubCriteriaEvaluationXService {
  private readonly directusService = inject(DirectusService);
  private readonly criteriaController = inject(EvaluationCriteriaControllerService);
  private readonly comparisonController = inject(EvaluationSubCriteriaComparisonControllerService);
  private readonly subCriteriaEvaluationService = inject(SubCriteriaEvaluationService);
  private readonly weightingController = inject(EvaluationSubCriteriaWeightingControllerService);

  loadCriteria(projectId: number): Observable<EvaluationCriteriaResponse[]> {
    return this.criteriaController
      .getEvaluationCriteriaByProject({projectId})
      .pipe(map((criteria) => [...criteria].sort((a, b) => a.ecNumber - b.ecNumber)));
  }

  loadDirectusCriteria(): Observable<EvaluationCriteria[]> {
    return this.directusService.loadEvaluationCriteria();
  }

  loadDirectusSubCriteria(): Observable<EvaluationSubCriteriaDirectus[]> {
    return this.directusService.loadSubCriteria();
  }

  loadComparisons(projectId: number): Observable<PairwiseComparisonsResponse> {
    return this.comparisonController.getEvaluationSubCriteriaComparisons({projectId});
  }

  saveComparisons(
    projectId: number,
    comparisons: PairwiseComparisonRequest[],
  ): Observable<PairwiseComparisonsResponse> {
    return this.comparisonController.setEvaluationSubCriteriaComparisons({
      projectId,
      setPairwiseComparisonsRequest: {
        comparisons,
        chapterTitle: ProjectChapterType.EVALUATION_CRITERIA,
        stepTitle: ProjectStepType.EVALUATION_SUB_CRITERIA,
      },
    });
  }

  saveInclusions(
    projectId: number,
    ecNumber: number,
    inclusions: SubCriteriaInclusionX[],
  ): Observable<EvaluationSubCriteriaResponse> {
    return this.subCriteriaEvaluationService.saveSubCriteriaEvaluations(projectId, ecNumber, {
      subCriteria: inclusions.map((inclusion) => ({
        ...(inclusion.id != null && {id: inclusion.id}),
        directusId: inclusion.directusId,
        scNumber: inclusion.scNumber,
        included: inclusion.included,
      })),
      chapterTitle: ProjectChapterType.EVALUATION_CRITERIA,
      stepTitle: ProjectStepType.EVALUATION_SUB_CRITERIA,
    });
  }

  saveWeighting(
    projectId: number,
    evaluationCriteriaId: number,
    request: UpsertCriteriaWeightingRequest,
  ): Observable<CriteriaWeightingResponse> {
    return this.weightingController.upsertEvaluationSubCriteriaWeighting({
      projectId,
      evaluationCriteriaId,
      upsertCriteriaWeightingRequest: request,
    });
  }
}
